import CommonModal from "../../Utility/CommonModel/CommonModal";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import Checkbox from "../../Utility/Checkbox/Checkbox";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import Label from "../../Utility/Label/Label";
import Input from "../../Utility/Input/Input";

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {Array<{ value: string, label: string }>} props.monthOptions — value like `January|2025`
 * @param {string} props.monthValue
 * @param {(value: string) => void} props.onMonthChange
 * @param {string[]} props.clientOptions — first entry should be `"Global"`
 * @param {string} props.clientValue
 * @param {(value: string) => void} props.onClientChange
 * @param {boolean} props.useAutoLock — maps to API `useAutoLock` (cron auto-lock later)
 * @param {(checked: boolean) => void} props.onUseAutoLockChange
 * @param {() => void} props.onConfirm
 * @param {boolean} [props.loading]
 */
export const LockMonthModal = ({
  isOpen,
  onClose,
  monthOptions = [],
  monthValue,
  onMonthChange,
  clientOptions = [],
  clientValue,
  onClientChange,
  useAutoLock = false,
  onUseAutoLockChange,
  onConfirm,
  errorMessage = "",
  loading = false,
}) => {
  const canSubmit =
    Boolean(monthValue) &&
    Boolean(clientValue) &&
    monthOptions.length > 0 &&
    clientOptions.length > 0;

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      title="Lock Month"
      width="max-w-lg"
      loading={loading}
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!loading && canSubmit) onConfirm?.();
        }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Dropdown
            label="Month"
            name="lockMonth"
            value={monthValue}
            onChange={(e) => onMonthChange(e.target.value)}
            options={monthOptions}
            placeholder="Select month"
            disabled={loading || monthOptions.length === 0}
            searchable
            clearable={false}
            preserveOptionOrder
          />
          <Dropdown
            label="Client"
            name="lockClient"
            value={clientValue}
            onChange={(e) => onClientChange(e.target.value)}
            options={clientOptions}
            placeholder="Select client"
            disabled={loading || clientOptions.length === 0}
            searchable
            clearable={false}
          />
        </div>

        {monthOptions.length === 0 && (
          <Typography variant="text2" className="text-text-secondary">
            No month periods are available for the current data. Refresh or contact an
            administrator.
          </Typography>
        )}
        {errorMessage && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
            <Typography className="text-red-700 text-sm">{errorMessage}</Typography>
          </div>
        )}

        <div className="flex items-center justify-between rounded-md bg-surface-secondary px-3 py-2">
          <div>
            <Typography variant="caption" className="uppercase tracking-wide text-text-secondary">
              Auto-lock schedule
            </Typography>
            <Typography variant="text2" className="text-text-primary">
              Use default billing lock schedule
            </Typography>
          </div>
          <Checkbox
            name="autoLockSchedule"
            checked={useAutoLock}
            onChange={(e) => onUseAutoLockChange?.(e.target.checked)}
            disabled={loading}
            noWrapper
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="sm" disabled={loading || !canSubmit}>
            Lock Month
          </Button>
        </div>
      </form>
    </CommonModal>
  );
};

export const UnlockMonthModal = ({
  isOpen,
  onClose,
  monthLabel,
  client,
  onConfirm,
  reason,
  setReason,
  errorMessage = "",
  loading = false,
}) => {
  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      title="Unlock Month"
      width="max-w-lg"
      loading={loading}
    >
      <div className="space-y-4">
        <Typography variant="text1" className="text-text-secondary">
          You are about to unlock{" "}
          <Typography variant="span2" className="font-semibold text-text-primary">
            {monthLabel}
          </Typography>{" "}
          for{" "}
          <Typography variant="span2" className="font-semibold text-text-primary">
            {client}
          </Typography>{" "}
          from billing edits. Please provide a reason for this action.
        </Typography>

        <div>
          <Label
            htmlFor="unlockReason"
            className="text-xs font-medium text-text-secondary mb-1"
            required
          >
            Reason for unlock
          </Label>

          <Input
            id="unlockReason"
            type="textarea"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Add details so there's a clear audit trail..."
            className="
          w-full rounded-md
          border border-border-primary
          bg-surface-secondary
          px-3 py-2 text-sm text-text-primary
          focus:outline-none focus:ring-2 focus:ring-primary-500
        "
          />
        </div>
        {errorMessage && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
            <Typography className="text-red-700 text-sm">{errorMessage}</Typography>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onConfirm}
            disabled={loading || !reason.trim()}
          >
            Unlock Month
          </Button>
        </div>
      </div>
    </CommonModal>
  );
};

export default {
  LockMonthModal,
  UnlockMonthModal,
};
